
import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Skeleton } from '@/components/ui/skeleton';
import { TokenUsage } from '../types';
import { ChartContainer, ChartTooltipContent } from '@/components/ui/chart';

interface TokenUsageChartProps {
  data: TokenUsage[];
  isLoading: boolean;
  height?: number;
  simplified?: boolean;
}

export const TokenUsageChart: React.FC<TokenUsageChartProps> = ({ 
  data, 
  isLoading, 
  height = 400,
  simplified = false 
}) => {
  if (isLoading) {
    return <Skeleton className="w-full" style={{ height }} />;
  }

  // Format dates to be more readable
  const formattedData = data.map(item => ({
    ...item,
    date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }));

  const chartConfig = {
    completion: {
      label: "Completion",
      color: "#9b87f5" // Royal purple
    },
    embedding: {
      label: "Embedding",
      color: "#0ac389" // Emerald green
    },
    total: {
      label: "Total",
      color: "#64B5F6" // Blue
    }
  };

  return (
    <ChartContainer config={chartConfig} className="w-full" style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={formattedData}
          margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
          barSize={simplified ? 12 : 20}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#2d2d2d" />
          <XAxis 
            dataKey="date" 
            tick={{ fill: '#9CA3AF' }} 
            axisLine={{ stroke: '#2d2d2d' }}
            tickLine={{ stroke: '#2d2d2d' }}
          />
          <YAxis 
            tick={{ fill: '#9CA3AF' }}
            axisLine={{ stroke: '#2d2d2d' }}
            tickLine={{ stroke: '#2d2d2d' }}
          />
          <Tooltip content={<ChartTooltipContent />} />
          <Legend 
            verticalAlign="top" 
            wrapperStyle={{ paddingBottom: 10 }}
          />
          <Bar 
            dataKey="completion" 
            name="Completion" 
            fill="var(--color-completion)" 
            radius={[4, 4, 0, 0]} 
          />
          <Bar 
            dataKey="embedding" 
            name="Embedding" 
            fill="var(--color-embedding)" 
            radius={[4, 4, 0, 0]} 
          />
          {simplified && (
            <Bar 
              dataKey="total" 
              name="Total" 
              fill="var(--color-total)" 
              radius={[4, 4, 0, 0]} 
              opacity={0.2} 
            />
          )}
        </BarChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
};
